/**
 * Show the teeinblue customization design image in the Section Store cart drawer.
 *
 * Why this exists: the Section Store cart (<section-store--cart-block>) renders inside a
 * shadow root, so teeinblue's cartContainerSelectors can't reach it, and the app always
 * uses the product/variant image. This script reads the `_customization_image` line item
 * property that teeinblue saves, and swaps the thumbnail for each matching cart line.
 *
 * Safety: teeinblue creates the customization image a moment AFTER add-to-cart (the URL can
 * return 403/404 for the first second or two, longer for big sizes). So we never swap blindly:
 * we preload the image in the background, keep the normal product image showing until the
 * design image has really loaded, and retry if it isn't ready yet.
 *
 * Speed: teeinblue only offers the full 960px design image (about 200 KB). For the tiny cart
 * thumbnail we load a small resized copy (about 10 KB) through the free wsrv.nl image proxy.
 * If the proxy fails for any reason we fall back to the full image, so nothing breaks.
 * To turn the proxy off (for example for privacy), set USE_RESIZER to false.
 */
(function () {
  'use strict';

  if (window._tibCartImageLoaded) return;
  window._tibCartImageLoaded = true;

  var HOST_TAG = 'section-store--cart-block';
  var PROP = '_customization_image';
  var MAX_TRIES = 15;        // how many times to retry a design image that isn't ready
  var RETRY_MS = 1200;       // wait between retries
  var USE_RESIZER = true;    // load a small resized copy for the cart thumbnail
  var THUMB_SIZE = 240;      // thumbnail size in px (2x-3x the ~90px it is shown at)

  var imageByKey = {};   // cart line key -> customization image URL
  var knownKeys = {};    // every line key we have seen in /cart.js
  var ready = {};        // design URL -> URL that is confirmed to load
  var pending = {};      // design URL -> true while we are still trying to load it
  var fetching = false;
  var refetchQueued = false;
  var root = null;
  var rafId = null;

  function loadCart(done) {
    if (fetching) { refetchQueued = true; return; }
    fetching = true;
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var images = {};
        var known = {};
        (cart.items || []).forEach(function (item) {
          known[item.key] = true;
          var url = item.properties && item.properties[PROP];
          if (url) images[item.key] = url;
        });
        imageByKey = images;
        knownKeys = known;
      })
      .catch(function () {})
      .then(function () {
        fetching = false;
        if (typeof done === 'function') done();
        if (refetchQueued) { refetchQueued = false; loadCart(applyImages); }
      });
  }

  function smallUrl(src) {
    return 'https://wsrv.nl/?url=' + encodeURIComponent(src.replace(/^https?:\/\//, '')) +
      '&w=' + THUMB_SIZE + '&h=' + THUMB_SIZE + '&fit=cover&output=webp&q=70';
  }

  function loadImage(src, onOk, onFail) {
    var img = new Image();
    img.onload = function () { if (img.naturalWidth > 0) onOk(src); else onFail(); };
    img.onerror = onFail;
    img.src = src;
  }

  // Try to load the design image in the background. Only when it loads for real do we mark it ready.
  function preload(url) {
    if (ready[url] || pending[url]) return;
    pending[url] = true;
    var tries = 0;

    function done(loadedSrc) {
      ready[url] = loadedSrc;
      pending[url] = false;
      schedule();
    }

    function retry() {
      if (tries >= MAX_TRIES) { pending[url] = false; return; } // give up, keep the normal product image
      setTimeout(attempt, RETRY_MS);
    }

    function attempt() {
      tries++;
      var fullUrl = tries === 1 ? url : url + (url.indexOf('?') > -1 ? '&' : '?') + 'r=' + tries;
      if (USE_RESIZER) {
        // small copy first; if the proxy can't deliver it, try the full image directly
        loadImage(smallUrl(fullUrl), done, function () { loadImage(fullUrl, done, retry); });
      } else {
        loadImage(fullUrl, done, retry);
      }
    }

    attempt();
  }

  function applyImages() {
    if (!root) return;
    var needsRefetch = false;

    root.querySelectorAll('.cart-line-item[data-line-key]').forEach(function (row) {
      var key = row.getAttribute('data-line-key');
      if (!knownKeys[key]) { needsRefetch = true; return; }

      var url = imageByKey[key];
      if (!url) return; // not a customized product: leave the default image alone

      var wrap = row.querySelector('.cart-line-image');
      if (!wrap) return;
      var img = wrap.querySelector('img');
      if (!img) return;

      var good = ready[url];
      if (!good) {
        preload(url); // keep the normal image showing until the design image is confirmed
        // If an earlier swap left a broken image behind, put the normal product image back.
        var orig = img.getAttribute('data-tib-orig');
        if (orig && img.getAttribute('src') !== orig) img.setAttribute('src', orig);
        return;
      }

      if (img.getAttribute('src') !== good) {
        if (!img.getAttribute('data-tib-orig')) img.setAttribute('data-tib-orig', img.getAttribute('src') || '');
        img.removeAttribute('srcset');
        img.setAttribute('src', good);
      }
    });

    if (needsRefetch) loadCart(applyImages);
  }

  function schedule() {
    if (rafId) return;
    rafId = requestAnimationFrame(function () {
      rafId = null;
      applyImages();
    });
  }

  function attach(host) {
    root = host.shadowRoot;
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    loadCart(applyImages);
  }

  // The cart element is created after this script loads, so wait for it.
  var tries = 0;
  var timer = setInterval(function () {
    var host = document.querySelector(HOST_TAG);
    if (host && host.shadowRoot) {
      clearInterval(timer);
      attach(host);
    } else if (++tries > 100) {
      clearInterval(timer);
    }
  }, 200);
})();